import React from 'react'
import { Button, HStack, Tooltip, useDisclosure } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/icons'
import { FiFileText } from 'react-icons/fi'

import {
    ChatMessageFieldsFragment,
    MessageMethod,
    useGetClinicQuery,
} from '~graphql/generated/graphql'
import CallDetailModal from '~components/calls/all/table/call-detail-modal'
import CallRecordingButton from '~components/calls/call-recording-button'

interface Props {
    message: ChatMessageFieldsFragment
}

/**
 * Recording playback + transcript viewer for phone-call system messages
 * (the 📞 entries the phone integrations log to chat). Renders nothing for
 * other system messages, or while the call has neither artifact yet
 * (recordings/transcripts typically land minutes after hangup).
 */
export default function SystemMessageCallMedia({ message }: Props) {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const call = message.call
    const isPhoneCall = !!message.message_methods?.includes(
        MessageMethod.PhoneCall,
    )

    // Full clinic row powers the modal's Teach-the-Agent section for AI calls.
    // Fetched only when the modal opens (chat fragments carry a slim clinic).
    const { data: clinicData } = useGetClinicQuery({
        variables: { id: message.chat.clinic.id },
        skip: !isOpen || !call?.is_automated,
    })

    if (!isPhoneCall || !call) return null
    if (!call.recording_url && !call.transcription_full_text) return null

    return (
        <HStack mt={2} spacing={2}>
            {call.recording_url && (
                <CallRecordingButton
                    recordingUrl={call.recording_url}
                    size="xs"
                />
            )}
            <Tooltip
                label={
                    call.transcription_full_text
                        ? 'View full transcript, QA score, and details'
                        : 'Transcript not available yet'
                }
                hasArrow
            >
                {/* Span wrapper: disabled buttons swallow mouse events, which
                would hide the tooltip explaining the transcript is pending. */}
                <span>
                    <Button
                        size="xs"
                        variant="outline"
                        colorScheme="brand"
                        leftIcon={<Icon as={FiFileText} />}
                        onClick={onOpen}
                        isDisabled={!call.transcription_full_text}
                    >
                        View transcript
                    </Button>
                </span>
            </Tooltip>
            <CallDetailModal
                isOpen={isOpen}
                onClose={onClose}
                call={call}
                clinic={clinicData?.clinic ?? null}
            />
        </HStack>
    )
}
